/**
 * Revenue Analytics utilities for the Breck Vipers admin dashboard.
 *
 * Data model notes (verified against the codebase):
 * - `orders` collection stores monetary values in DOLLARS (not cents). The
 *   Stripe webhook (`functions/src/handleStripeWebhook.ts`) divides Stripe's
 *   cent amounts by 100 before persisting `financialInfo` and `items[].priceEach`.
 * - `orderStatus` is a NUMBER: 1=New, 2=Processing, 3=Shipped, 4=Delivered,
 *   5=Cancelled, 6=Refunded (see components/admin/EnhancedMerchandiseTable.tsx).
 * - `paymentStatus` is a STRING; a completed sale is `'paid'`.
 * - Revenue counts PAID orders only: `paymentStatus === 'paid'` AND
 *   `orderStatus` NOT in {5 Cancelled, 6 Refunded}.
 * - Ticket line items are linked to games through `qrTickets[]`: each qrTicket
 *   carries `itemIndex` (position in `items[]`) and `eventId` (the game doc id).
 *   We resolve the game name via the `games` collection, falling back to the
 *   ticket name when a qrTicket/game is unavailable.
 *
 * This module performs NO writes. It only reads Firestore and produces
 * in-memory aggregates and client-side CSV strings.
 */

import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firestore';
import type { Order, Game } from '../types/global';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** orderStatus numbers that must be EXCLUDED from revenue (cancelled/refunded). */
export const EXCLUDED_ORDER_STATUSES: ReadonlySet<number> = new Set([5, 6]);

/** Human-readable order status labels, indexed by orderStatus number. */
export const ORDER_STATUS_LABELS: Record<number, string> = {
  1: 'New',
  2: 'Processing',
  3: 'Shipped',
  4: 'Delivered',
  5: 'Cancelled',
  6: 'Refunded',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateRange {
  /** Inclusive start of the window. */
  start: Date;
  /** Inclusive end of the window. */
  end: Date;
  /** Human label for headings / export filenames (e.g. "2024", "All time"). */
  label: string;
}

/** One fully-flattened order line item, ready for detail export or display. */
export interface LineItemRow {
  orderId: string;
  orderDateISO: string;
  orderDate: Date | null;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  itemName: string;
  category: string;
  type: 'ticket' | 'merchandise' | string;
  quantity: number;
  priceEach: number;
  lineTotal: number;
  size: string;
  color: string;
  linkedGameName: string;
  gameKey: string;
  gameOpponent: string;
  eventYear: number | null;
  eventDateISO: string;
  eventLocation: string;
  orderSubtotal: number;
  processingFee: number;
  shippingCost: number;
  orderTotal: number;
  paymentStatus: string;
  orderStatusCode: number;
  orderStatusLabel: string;
}

/** A grouped summary row (used for game / product / category breakdowns). */
export interface SummaryRow {
  key: string;
  label: string;
  /** Secondary descriptor, e.g. event date for a game or "ticket"/"merch". */
  sublabel?: string;
  revenue: number;
  quantity: number;
  orderCount: number;
}

/** Ticket-type units + revenue within a single game. */
export interface TicketTypeInGame {
  type: string;
  units: number;
  revenue: number;
}

/** A fully-detailed game: metadata + tickets sold, broken down by ticket type. */
export interface GameSummary {
  key: string;
  name: string;
  opponent: string;
  eventDateISO: string;
  /** Calendar year the game is played (from event date). */
  year: number | null;
  eventLocation: string;
  ticketUnits: number;
  ticketRevenue: number;
  orderCount: number;
  byType: TicketTypeInGame[];
}

/** Revenue rolled up by calendar year of sale. */
export interface YearSummary {
  year: number;
  totalRevenue: number;
  ticketRevenue: number;
  merchRevenue: number;
  units: number;
  orderCount: number;
}

/** One row of the tickets game×type cross-breakdown. */
export interface TicketByGameTypeRow {
  gameName: string;
  opponent: string;
  eventDateISO: string;
  year: number | null;
  ticketType: string;
  units: number;
  revenue: number;
}

export interface RevenueAnalytics {
  range: DateRange;
  /** Every paid line item within the range (source of truth for the detail export). */
  lineItems: LineItemRow[];
  metrics: {
    totalRevenue: number;
    ticketRevenue: number;
    merchRevenue: number;
    orderCount: number;
    itemsSold: number;
    averageOrderValue: number;
  };
  byGame: SummaryRow[];
  byTicketType: SummaryRow[];
  byMerchCategory: SummaryRow[];
  byProduct: SummaryRow[];
  /** Enhanced breakdowns (Excel workbook export). */
  byYear: YearSummary[];
  games: GameSummary[];
  ticketsByGameType: TicketByGameTypeRow[];
}

export interface LoadedRevenueData {
  orders: Order[];
  gamesById: Map<string, Game>;
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

/** Coerce a Firestore Timestamp / plain {seconds} / Date / string into a Date. */
export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'object') {
    const v = value as { seconds?: number; _seconds?: number; toDate?: () => Date };
    if (typeof v.toDate === 'function') {
      try {
        const d = v.toDate();
        return isNaN(d.getTime()) ? null : d;
      } catch {
        /* fall through */
      }
    }
    const secs = typeof v.seconds === 'number' ? v.seconds
      : typeof v._seconds === 'number' ? v._seconds
      : null;
    if (secs !== null) return new Date(secs * 1000);
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function toISO(value: unknown): string {
  const d = toDate(value);
  return d ? d.toISOString() : '';
}

/** Build the DateRange for a given full calendar year. */
export function yearRange(year: number): DateRange {
  return {
    start: new Date(year, 0, 1, 0, 0, 0, 0),
    end: new Date(year, 11, 31, 23, 59, 59, 999),
    label: String(year),
  };
}

/** The default range: the previous calendar year ("last year"). */
export function defaultDateRange(now: Date = new Date()): DateRange {
  return yearRange(now.getFullYear() - 1);
}

/** An all-encompassing range (used for the "All time" preset). */
export function allTimeRange(): DateRange {
  return {
    start: new Date(1970, 0, 1),
    end: new Date(9999, 11, 31, 23, 59, 59, 999),
    label: 'All time',
  };
}

// ---------------------------------------------------------------------------
// Revenue eligibility
// ---------------------------------------------------------------------------

/** True when an order represents realized, paid revenue. */
export function isPaidOrder(order: Order): boolean {
  const paid = (order.paymentStatus || '').toLowerCase() === 'paid';
  const status = Number(order.orderStatus);
  const excluded = !Number.isNaN(status) && EXCLUDED_ORDER_STATUSES.has(status);
  return paid && !excluded;
}

function inRange(date: Date | null, range: DateRange): boolean {
  if (!date) return false;
  return date.getTime() >= range.start.getTime() && date.getTime() <= range.end.getTime();
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

/** Load orders + games from Firestore (read-only). */
export async function loadRevenueData(): Promise<LoadedRevenueData> {
  const [ordersSnap, gamesSnap] = await Promise.all([
    getDocs(collection(db, 'orders')),
    getDocs(collection(db, 'games')),
  ]);

  const orders: Order[] = ordersSnap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<Order, 'id'>),
  }));

  const gamesById = new Map<string, Game>();
  gamesSnap.docs.forEach((d) => {
    gamesById.set(d.id, { id: d.id, ...(d.data() as Omit<Game, 'id'>) });
  });

  return { orders, gamesById };
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

interface ResolvedGame {
  key: string;
  name: string;
  opponent: string;
  eventDateISO: string;
  year: number | null;
  eventLocation: string;
}

/**
 * Whether a line item is a TICKET. The authoritative signal is the category
 * ("ticket"), because many historical ticket line items have `type` unset
 * (or wrong) — only ~half carry type === 'ticket'. Category is reliable.
 */
export function isTicketItem(type?: string, category?: string): boolean {
  return (
    (type || '').toLowerCase() === 'ticket' ||
    (category || '').toLowerCase() === 'ticket'
  );
}

/**
 * Whether a line item is a fee/shipping charge rather than a sold product.
 * These must be excluded from product revenue and unit counts (they're already
 * reported separately from financialInfo on each detail row).
 */
export function isNonProductItem(type?: string, category?: string): boolean {
  const t = (type || '').toLowerCase();
  const c = (category || '').toLowerCase();
  return t === 'processing_fee' || t === 'fee' || c === 'fee' || c === 'shipping';
}

/** Normalize a game/matchup name so ticket item names match games collection names. */
function normGameName(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/\bbreckenridge\b/g, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\bv\b/g, 'vs') // "Vipers v X" -> "vipers vs x"
    .replace(/\s+/g, ' ')
    .trim();
}

/** Looser key that ignores trailing plurals ("Barracudas" ~ "Barracuda"). */
function normLooseName(s: string): string {
  return normGameName(s)
    .split(' ')
    .map((w) => (w !== 'vs' && w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w))
    .join(' ');
}

/** Build a normalized-name -> Game index (exact + plural-insensitive keys). */
function buildGamesByName(gamesById: Map<string, Game>): Map<string, Game> {
  const byName = new Map<string, Game>();
  for (const g of gamesById.values()) {
    const exact = normGameName(g.name);
    const loose = normLooseName(g.name);
    if (exact && !byName.has(exact)) byName.set(exact, g);
    if (loose && !byName.has(loose)) byName.set(loose, g);
  }
  return byName;
}

/**
 * Strip ticket "decorations" so a ticket item name reduces to its core matchup:
 * seat-type prefixes ("General Admission - "), a trailing SKYBOX/VIP tag, and any
 * embedded date/time tokens ("3/20/26", "730P"). Lets more items match a game.
 */
function stripTicketDecorations(name: string): string {
  return (name || '')
    .replace(/^\s*(general admission|premium seats?|season ticket|vip|skybox)\s*[-:]\s*/i, '')
    .replace(/\bskybox\b/gi, ' ')
    .replace(/\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/g, ' ') // dates
    .replace(/\b\d{1,2}:?\d{0,2}\s*[ap]\.?m?\.?\b/gi, ' ') // times: 730p, 7:30pm
    .replace(/\s+/g, ' ')
    .trim();
}

/** Parse a date embedded in a ticket name (e.g. "3/20/26", "12/19/2025"). Needs a year. */
function parseEmbeddedDate(name: string): Date | null {
  const m = (name || '').match(/\b(\d{1,2})\/(\d{1,2})\/(\d{2,4})\b/);
  if (!m) return null;
  const mo = parseInt(m[1], 10);
  const da = parseInt(m[2], 10);
  let yr = parseInt(m[3], 10);
  if (yr < 100) yr += 2000;
  if (mo < 1 || mo > 12 || da < 1 || da > 31) return null;
  const d = new Date(yr, mo - 1, da);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Curated aliases for historical ticket labels that are confidently a known game
 * but whose name doesn't match any game record. Confirmed with the schedule owner.
 * Key = normalized ticket name, value = normalized target game name.
 */
const NAME_ALIASES: Record<string, string> = {
  // "Vipers vs Ice Raiders" tickets (ordered Nov 5–23, 2025) = the Reno Ice
  // Raiders home series on Nov 21–22, 2025.
  'vipers vs ice raiders': 'vipers vs reno ice raiders',
};

/** Look up a game by matchup name (alias, then exact, then plural-insensitive). */
function matchGameByName(name: string, gamesByName: Map<string, Game>): Game | undefined {
  const aliasKey = NAME_ALIASES[normGameName(name)];
  return (
    (aliasKey ? gamesByName.get(aliasKey) : undefined) ||
    gamesByName.get(normGameName(name)) ||
    gamesByName.get(normLooseName(name))
  );
}

/**
 * Resolve which game a ticket line item belongs to. The qrTicket `eventId` is a
 * ticket-PRODUCT id (not a game doc id), so the game/date is resolved by matching
 * the item's matchup name against the games collection. Falls back gracefully.
 */
function resolveGameForItem(
  order: Order,
  itemIndex: number,
  gamesById: Map<string, Game>,
  gamesByName: Map<string, Game>
): ResolvedGame | null {
  const item = order.items?.[itemIndex];
  if (!item || !isTicketItem(item.type, item.category)) return null;

  const qr = order.qrTickets?.find((t) => t.itemIndex === itemIndex);
  const eventId = qr?.eventId;
  const rawName = item.name || '';
  // eventId occasionally IS a game id; otherwise match the matchup name, then
  // retry against the decoration-stripped name (drops SKYBOX / seat / date tags).
  const game =
    (eventId ? gamesById.get(eventId) : undefined) ||
    matchGameByName(rawName, gamesByName) ||
    matchGameByName(stripTicketDecorations(rawName), gamesByName);

  const name = game?.name || rawName || qr?.name || 'Unknown game';
  const key = game?.id || `name:${normGameName(stripTicketDecorations(rawName) || name)}`;

  // Date priority: matched game -> qr date -> date embedded in the ticket name.
  let eventDateISO = game ? toISO(game.eventDate) : (qr?.eventDate || '');
  if (!eventDateISO) {
    const embedded = parseEmbeddedDate(rawName);
    if (embedded) eventDateISO = embedded.toISOString();
  }

  const eventLocation = game?.eventLocation || qr?.eventLocation || '';
  const opponent = game?.opponent || '';
  const ed = eventDateISO ? new Date(eventDateISO) : null;
  const year = ed && !Number.isNaN(ed.getTime()) ? ed.getFullYear() : null;

  return { key, name, opponent, eventDateISO, year, eventLocation };
}

/** Flatten every paid order in the range into detail line-item rows. */
export function buildLineItemRows(
  orders: Order[],
  gamesById: Map<string, Game>,
  range: DateRange
): LineItemRow[] {
  const rows: LineItemRow[] = [];
  const gamesByName = buildGamesByName(gamesById);

  for (const order of orders) {
    if (!isPaidOrder(order)) continue;
    const orderDate = toDate(order.orderDate);
    if (!inRange(orderDate, range)) continue;

    const fin = order.financialInfo || ({} as Order['financialInfo']);
    const billing = order.billingInfo || ({} as Order['billingInfo']);
    const statusCode = Number(order.orderStatus);
    const items = order.items || [];

    items.forEach((item, index) => {
      // Fee / shipping line items are not sold products — skip them entirely so
      // they never inflate merch revenue or unit counts.
      if (isNonProductItem(item.type, item.category)) return;

      const quantity = Number(item.quantity) || 0;
      const priceEach = Number(item.priceEach) || 0;
      const ticket = isTicketItem(item.type, item.category);
      const game = resolveGameForItem(order, index, gamesById, gamesByName);

      rows.push({
        orderId: order.orderId || order.id,
        orderDateISO: orderDate ? orderDate.toISOString() : '',
        orderDate,
        customerFirstName: billing.firstName || '',
        customerLastName: billing.lastName || '',
        customerEmail: billing.email || '',
        itemName: item.name || '',
        category: item.category || '',
        // Normalized so downstream ticket/merch splits are correct even when the
        // stored `type` is missing or wrong (category is the source of truth).
        type: ticket ? 'ticket' : 'merchandise',
        quantity,
        priceEach,
        lineTotal: Math.round(priceEach * quantity * 100) / 100,
        size: item.size || '',
        color: item.color || '',
        linkedGameName: game?.name || '',
        gameKey: game?.key || '',
        gameOpponent: game?.opponent || '',
        eventYear: game?.year ?? null,
        eventDateISO: game?.eventDateISO || '',
        eventLocation: game?.eventLocation || '',
        orderSubtotal: Number(fin.subtotal) || 0,
        processingFee: Number(fin.processingFee) || 0,
        shippingCost: Number(fin.shippingCost) || 0,
        orderTotal: Number(fin.total) || 0,
        paymentStatus: order.paymentStatus || '',
        orderStatusCode: Number.isNaN(statusCode) ? 0 : statusCode,
        orderStatusLabel: ORDER_STATUS_LABELS[statusCode] || 'Unknown',
      });
    });
  }

  return rows;
}

function accumulate(
  map: Map<string, SummaryRow>,
  key: string,
  label: string,
  revenue: number,
  quantity: number,
  orderId: string,
  seenOrders: Map<string, Set<string>>,
  sublabel?: string
): void {
  let row = map.get(key);
  if (!row) {
    row = { key, label, sublabel, revenue: 0, quantity: 0, orderCount: 0 };
    map.set(key, row);
    seenOrders.set(key, new Set());
  }
  row.revenue += revenue;
  row.quantity += quantity;
  if (sublabel && !row.sublabel) row.sublabel = sublabel;
  const orders = seenOrders.get(key)!;
  if (!orders.has(orderId)) {
    orders.add(orderId);
    row.orderCount += 1;
  }
}

function sortByRevenue(rows: SummaryRow[]): SummaryRow[] {
  return rows.sort((a, b) => b.revenue - a.revenue);
}

/** Compute the full analytics bundle for a date range. */
export function computeAnalytics(
  orders: Order[],
  gamesById: Map<string, Game>,
  range: DateRange
): RevenueAnalytics {
  const lineItems = buildLineItemRows(orders, gamesById, range);

  // Metrics
  let ticketRevenue = 0;
  let merchRevenue = 0;
  let itemsSold = 0;
  const paidOrderIds = new Set<string>();

  // Breakdown accumulators
  const gameMap = new Map<string, SummaryRow>();
  const gameOrders = new Map<string, Set<string>>();
  const ticketTypeMap = new Map<string, SummaryRow>();
  const ticketTypeOrders = new Map<string, Set<string>>();
  const merchCatMap = new Map<string, SummaryRow>();
  const merchCatOrders = new Map<string, Set<string>>();
  const productMap = new Map<string, SummaryRow>();
  const productOrders = new Map<string, Set<string>>();
  const gameSummaryMap = new Map<string, GameSummary>();
  const gameSeenOrders = new Map<string, Set<string>>();
  const yearMap = new Map<number, YearSummary>();
  const yearSeenOrders = new Map<number, Set<string>>();

  for (const row of lineItems) {
    paidOrderIds.add(row.orderId);
    itemsSold += row.quantity;

    const isTicket = row.type === 'ticket';
    if (isTicket) ticketRevenue += row.lineTotal;
    else merchRevenue += row.lineTotal;

    // By year (of sale)
    const saleYear = row.orderDate ? row.orderDate.getFullYear() : null;
    if (saleYear !== null) {
      let ys = yearMap.get(saleYear);
      if (!ys) {
        ys = { year: saleYear, totalRevenue: 0, ticketRevenue: 0, merchRevenue: 0, units: 0, orderCount: 0 };
        yearMap.set(saleYear, ys);
        yearSeenOrders.set(saleYear, new Set());
      }
      ys.totalRevenue += row.lineTotal;
      if (isTicket) ys.ticketRevenue += row.lineTotal; else ys.merchRevenue += row.lineTotal;
      ys.units += row.quantity;
      const yso = yearSeenOrders.get(saleYear)!;
      if (!yso.has(row.orderId)) { yso.add(row.orderId); ys.orderCount += 1; }
    }

    // By product (every line item)
    accumulate(
      productMap,
      `${row.type}:${row.itemName.toLowerCase()}`,
      row.itemName || 'Unnamed product',
      row.lineTotal,
      row.quantity,
      row.orderId,
      productOrders,
      isTicket ? 'Ticket' : 'Merchandise'
    );

    if (isTicket) {
      // By game
      const gameLabel = row.linkedGameName || row.itemName || 'Unknown game';
      const gameKey = row.linkedGameName
        ? `game:${row.linkedGameName.toLowerCase()}`
        : `ticket:${row.itemName.toLowerCase()}`;
      accumulate(
        gameMap,
        gameKey,
        gameLabel,
        row.lineTotal,
        row.quantity,
        row.orderId,
        gameOrders,
        row.eventDateISO ? row.eventDateISO.slice(0, 10) : undefined
      );

      // By ticket type (uses item name as the ticket-type descriptor)
      accumulate(
        ticketTypeMap,
        `tt:${row.itemName.toLowerCase()}`,
        row.itemName || 'Unknown ticket',
        row.lineTotal,
        row.quantity,
        row.orderId,
        ticketTypeOrders
      );

      // Detailed game summary with per-ticket-type breakdown
      const gKey = row.gameKey || `game:${(row.linkedGameName || row.itemName).toLowerCase()}`;
      let gs = gameSummaryMap.get(gKey);
      if (!gs) {
        gs = {
          key: gKey,
          name: row.linkedGameName || row.itemName || 'Unknown game',
          opponent: row.gameOpponent,
          eventDateISO: row.eventDateISO,
          year: row.eventYear,
          eventLocation: row.eventLocation,
          ticketUnits: 0, ticketRevenue: 0, orderCount: 0, byType: [],
        };
        gameSummaryMap.set(gKey, gs);
        gameSeenOrders.set(gKey, new Set());
      }
      gs.ticketUnits += row.quantity;
      gs.ticketRevenue += row.lineTotal;
      const gso = gameSeenOrders.get(gKey)!;
      if (!gso.has(row.orderId)) { gso.add(row.orderId); gs.orderCount += 1; }
      const tName = row.itemName || 'Unknown ticket';
      let tt = gs.byType.find((t) => t.type === tName);
      if (!tt) { tt = { type: tName, units: 0, revenue: 0 }; gs.byType.push(tt); }
      tt.units += row.quantity;
      tt.revenue += row.lineTotal;
    } else {
      // By merchandise category
      const cat = row.category || 'Uncategorized';
      accumulate(
        merchCatMap,
        `cat:${cat.toLowerCase()}`,
        cat,
        row.lineTotal,
        row.quantity,
        row.orderId,
        merchCatOrders
      );
    }
  }

  const totalRevenue = Math.round((ticketRevenue + merchRevenue) * 100) / 100;
  const orderCount = paidOrderIds.size;
  const r2 = (n: number) => Math.round(n * 100) / 100;

  const byYear: YearSummary[] = [...yearMap.values()]
    .sort((a, b) => b.year - a.year)
    .map((y) => ({ ...y, totalRevenue: r2(y.totalRevenue), ticketRevenue: r2(y.ticketRevenue), merchRevenue: r2(y.merchRevenue) }));

  const games: GameSummary[] = [...gameSummaryMap.values()]
    .sort((a, b) => b.ticketRevenue - a.ticketRevenue)
    .map((g) => ({
      ...g,
      ticketRevenue: r2(g.ticketRevenue),
      byType: g.byType.sort((a, b) => b.revenue - a.revenue).map((t) => ({ ...t, revenue: r2(t.revenue) })),
    }));

  const ticketsByGameType: TicketByGameTypeRow[] = [];
  for (const g of games) {
    for (const t of g.byType) {
      ticketsByGameType.push({
        gameName: g.name, opponent: g.opponent, eventDateISO: g.eventDateISO,
        year: g.year, ticketType: t.type, units: t.units, revenue: t.revenue,
      });
    }
  }

  return {
    range,
    lineItems,
    metrics: {
      totalRevenue,
      ticketRevenue: Math.round(ticketRevenue * 100) / 100,
      merchRevenue: Math.round(merchRevenue * 100) / 100,
      orderCount,
      itemsSold,
      averageOrderValue: orderCount ? Math.round((totalRevenue / orderCount) * 100) / 100 : 0,
    },
    byGame: sortByRevenue([...gameMap.values()]),
    byTicketType: sortByRevenue([...ticketTypeMap.values()]),
    byMerchCategory: sortByRevenue([...merchCatMap.values()]),
    byProduct: sortByRevenue([...productMap.values()]),
    byYear,
    games,
    ticketsByGameType,
  };
}

/** Distinct calendar years (descending) that contain at least one paid order. */
export function paidOrderYears(orders: Order[]): number[] {
  const years = new Set<number>();
  for (const order of orders) {
    if (!isPaidOrder(order)) continue;
    const d = toDate(order.orderDate);
    if (d) years.add(d.getFullYear());
  }
  return [...years].sort((a, b) => b - a);
}

/**
 * The season start-year a date belongs to. A hockey season spans two calendar
 * years and rolls over in July (matches src/globalVariables.ts), so e.g. any
 * date from Jul 2025 through Jun 2026 belongs to the 2025-26 season (start 2025).
 */
export function seasonStartYear(d: Date): number {
  return d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
}

/**
 * Full-season date range: Jul 1 (startYear) → Jun 30 (startYear + 1). This spans
 * BOTH calendar years so a season export never misses the spring half of games.
 * Label like "2025-26 Season".
 */
export function seasonRange(startYear: number): DateRange {
  return {
    start: new Date(startYear, 6, 1, 0, 0, 0, 0),
    end: new Date(startYear + 1, 5, 30, 23, 59, 59, 999),
    label: `${startYear}-${String(startYear + 1).slice(-2)} Season`,
  };
}

/** Distinct season start-years that have paid orders, newest first. */
export function paidOrderSeasons(orders: Order[]): number[] {
  const seasons = new Set<number>();
  for (const order of orders) {
    if (!isPaidOrder(order)) continue;
    const d = toDate(order.orderDate);
    if (d) seasons.add(seasonStartYear(d));
  }
  return [...seasons].sort((a, b) => b - a);
}

/**
 * The season carrying the most paid orders — the one an admin most likely wants
 * to open on. Unlike "newest season with any sales", a single stray/test order
 * in a not-yet-started season can't hijack this (it would need to be the busiest).
 * Ties break toward the newer season. Returns null when there are no paid orders.
 */
export function dominantPaidOrderSeason(orders: Order[]): number | null {
  const counts = new Map<number, number>();
  for (const order of orders) {
    if (!isPaidOrder(order)) continue;
    const d = toDate(order.orderDate);
    if (!d) continue;
    const sy = seasonStartYear(d);
    counts.set(sy, (counts.get(sy) || 0) + 1);
  }
  let best: number | null = null;
  let bestCount = -1;
  for (const [sy, count] of counts) {
    if (count > bestCount || (count === bestCount && best !== null && sy > best)) {
      best = sy;
      bestCount = count;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// CSV generation (Excel-safe: UTF-8 BOM + RFC-4180 quoting)
// ---------------------------------------------------------------------------

const UTF8_BOM = '﻿';

/** Escape a single CSV cell per RFC 4180. */
function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const isNumber = typeof value === 'number';
  let s = String(value);
  // Guard against CSV/formula injection in spreadsheet apps — but never mangle a
  // real negative number (e.g. -5.00 from a refund/adjustment).
  if (!isNumber && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\n\r]/.test(s)) {
    s = `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Build a full CSV string (with BOM) from a header row and data rows. */
export function toCSV(headers: string[], rows: Array<Array<unknown>>): string {
  const lines: string[] = [];
  lines.push(headers.map(csvCell).join(','));
  for (const row of rows) {
    lines.push(row.map(csvCell).join(','));
  }
  return UTF8_BOM + lines.join('\r\n');
}

function money(n: number): string {
  return (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);
}

/** Full line-item detail CSV (one row per order line item, exhaustive columns). */
export function buildDetailCSV(rows: LineItemRow[]): string {
  const headers = [
    'Order ID',
    'Order Date (ISO)',
    'Customer First Name',
    'Customer Last Name',
    'Customer Email',
    'Item Name',
    'Category',
    'Type',
    'Quantity',
    'Price Each',
    'Line Total',
    'Size',
    'Color',
    'Linked Game',
    'Event Date (ISO)',
    'Event Location',
    'Order Subtotal',
    'Processing Fee',
    'Shipping Cost',
    'Order Total',
    'Payment Status',
    'Order Status Code',
    'Order Status',
  ];
  const data = rows.map((r) => [
    r.orderId,
    r.orderDateISO,
    r.customerFirstName,
    r.customerLastName,
    r.customerEmail,
    r.itemName,
    r.category,
    r.type,
    r.quantity,
    money(r.priceEach),
    money(r.lineTotal),
    r.size,
    r.color,
    r.linkedGameName,
    r.eventDateISO,
    r.eventLocation,
    money(r.orderSubtotal),
    money(r.processingFee),
    money(r.shippingCost),
    money(r.orderTotal),
    r.paymentStatus,
    r.orderStatusCode,
    r.orderStatusLabel,
  ]);
  return toCSV(headers, data);
}

/** Generic summary CSV builder (revenue / units / orders per row). */
export function buildSummaryCSV(
  labelHeader: string,
  rows: SummaryRow[],
  includeSublabel = false,
  sublabelHeader = 'Detail'
): string {
  const headers = includeSublabel
    ? [labelHeader, sublabelHeader, 'Revenue', 'Units Sold', 'Orders']
    : [labelHeader, 'Revenue', 'Units Sold', 'Orders'];
  const data = rows.map((r) =>
    includeSublabel
      ? [r.label, r.sublabel || '', money(r.revenue), r.quantity, r.orderCount]
      : [r.label, money(r.revenue), r.quantity, r.orderCount]
  );
  return toCSV(headers, data);
}

// ---------------------------------------------------------------------------
// Client-side download
// ---------------------------------------------------------------------------

/** Trigger a client-side file download of a CSV string. No server round-trip. */
export function downloadCSV(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Filename-safe slug of a range label, e.g. "All time" -> "all-time". */
export function rangeSlug(range: DateRange): string {
  return (range.label || 'export')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
